import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { getBuildingNameClass, getSsDueTextClassFromDays } from '../../utils/ssDueColor';
import { daysUntilDue } from '../../utils/dueDate';
import { applyInspectionDue } from '../../utils/inspectionDueDate';
import { getJobRowStyle, JOB_STATUS_LABELS } from '../../utils/jobStatusColor';
import { useAuthStore } from '../../stores/authStore';
import {
  Calendar,
  User,
  Send,
  Mail,
  Phone,
  Pencil,
  Trash2,
  Printer,
  X,
  FileText,
  Users,
  ChevronRight,
  RotateCcw,
  Building2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import WorkerSelectionModal from './WorkerSelectionModal';
import ScheduleJobModal from './ScheduleJobModal';
import { InfoButton } from '../../components/InfoModal';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import SsPeriodLine from '../../components/SsPeriodLine';
import { formatDateForInput } from '../../utils/formatDateForInput';
import {
  JOB_DEFINITION,
  JOB_STATUS_ACTORS,
  getAdminNextAction,
  INSPECTION_LABELS,
  INSPECTION_OPS_STORY,
  getNextStageAction,
} from '../../constants/jobModel';
import ForceMoveControl from '../../components/ForceMoveControl';
import StickyActionFooter from '../../components/StickyActionFooter';
import { formatSsFrequencyChip } from '../../utils/inspectionLabel';
import { loadIssuedPaperworkPreview } from '../../utils/documentPreview';
import {
  captureWindowScroll,
  pinWindowScroll,
  preventFocusScroll,
} from '../../utils/windowScrollGuard';

function formatSsCodeLabel(code) {
  const s = String(code || '').trim();
  if (!s) return '';
  return /^ss\s/i.test(s) ? s : `SS ${s}`;
}

function typeBadgeClass(type) {
  if (type === 'FORM_12A') return 'bg-green-100 text-green-900';
  if (type === 'S_RAD') return 'bg-blue-100 text-blue-900';
  if (type === 'DEFECT_NOTICE') return 'bg-red-100 text-red-800';
  if (type === 'INSPECTION_REPORT') return 'bg-sky-100 text-sky-900';
  return 'bg-slate-100 text-slate-700';
}

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const detailTab = (() => {
    const t = (searchParams.get('tab') || 'building').toLowerCase();
    if (t === 'action' || t === 'certs' || t === 'certificates') return t === 'certificates' ? 'certs' : t;
    return 'building';
  })();
  const setDetailTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [editForm, setEditForm] = useState({
    due_date: '',
    scheduled_date: '',
    assigned_worker_id: '',
    status: '',
    inspection_type: '',
    inspection_frequency: '',
  });
  const [certPreviewId, setCertPreviewId] = useState(null);
  const [certPreviewHtml, setCertPreviewHtml] = useState('');
  const [certSendTo, setCertSendTo] = useState('');
  const [certBusy, setCertBusy] = useState(false);

  const isAdmin =
    user?.role === 'ADMIN' ||
    user?.role === 'MANAGER' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'MASTER';

  const { data: job, isLoading } = useQuery({
    queryKey: ['job', id],
    queryFn: async () => {
      const response = await api.get(`/jobs/${id}`);
      return response.data;
    },
    placeholderData: (prev) => prev,
    refetchInterval: editing ? false : 5000,
  });

  const { data: workersRaw } = useQuery({
    queryKey: ['workers'],
    queryFn: async () => {
      const response = await api.get('/workers', { params: { limit: 1000, status: 'all' } });
      return response.data;
    },
    enabled: isAdmin && id !== 'new',
  });

  const workersList = useMemo(() => {
    if (Array.isArray(workersRaw)) return workersRaw;
    if (Array.isArray(workersRaw?.data)) return workersRaw.data;
    return [];
  }, [workersRaw]);

  useEffect(() => {
    if (!job) return;
    setEditForm({
      due_date: formatDateForInput(applyInspectionDue(job).due_date),
      scheduled_date: formatDateForInput(job.scheduled_date),
      assigned_worker_id: job.assigned_worker_id || '',
      status: job.status || '',
      inspection_type: job.inspection_type || '',
      inspection_frequency: job.inspection_frequency || '',
    });
  }, [job]);

  const invalidateJob = () => {
    queryClient.invalidateQueries(['job', id]);
    queryClient.invalidateQueries(['jobs']);
    queryClient.invalidateQueries(['jobs', 'dashboard-summary']);
    queryClient.invalidateQueries(['jobs', 'building']);
    queryClient.invalidateQueries(['building-inspections']);
    queryClient.invalidateQueries(['schedules']);
    queryClient.invalidateQueries(['jobs', 'schedule-events']);
    queryClient.invalidateQueries(['bwof-workflow']);
  };

  const statusMutation = useMutation({
    mutationFn: async (status) => {
      const response = await api.patch(`/jobs/${id}`, {
        status,
        admin_note: adminNote.trim() || undefined,
      });
      return response.data;
    },
    onSuccess: (data, status) => {
      toast.success(
        status === 'COMPLETED'
          ? 'Inspection marked complete.'
          : `Status → ${JOB_STATUS_LABELS[status] || status}`
      );
      if (data?.bwof_message) toast.success(data.bwof_message);
      setAdminNote('');
      invalidateJob();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update status');
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await api.patch(`/jobs/${id}`, {
        ...payload,
        admin_note: adminNote.trim() || 'Admin edit',
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Inspection updated');
      if (data?.bwof_message) toast.success(data.bwof_message);
      setEditing(false);
      setAdminNote('');
      invalidateJob();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete(`/jobs/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Inspection deleted');
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs', 'dashboard-summary']);
      navigate('/jobs');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete');
    },
  });

  const handleSaveEdit = (e) => {
    e.preventDefault();
    saveMutation.mutate({
      due_date: editForm.due_date || null,
      scheduled_date: editForm.scheduled_date || null,
      assigned_worker_id: editForm.assigned_worker_id || null,
      status: editForm.status,
      inspection_type: editForm.inspection_type || undefined,
      inspection_frequency: editForm.inspection_frequency || undefined,
    });
  };

  const confirmStatus = (status, message) => {
    if (!window.confirm(message)) return;
    statusMutation.mutate(status);
  };

  const handleDelete = () => {
    if (
      !window.confirm(
        `Delete inspection (${job?.ss_code})? This cannot be undone.`
      )
    ) {
      return;
    }
    deleteMutation.mutate();
  };

  const ssCode = String(job?.ss_code || location.state?.ssCode || '').trim();
  const ssLabel = formatSsCodeLabel(ssCode);
  const buildingIdForTrail = String(
    job?.building_id || location.state?.buildingId || ''
  ).trim();

  const ssListTo = useMemo(() => {
    if (location.state?.origin?.to) return location.state.origin.to;
    if (buildingIdForTrail && ssCode) {
      return `/jobs/building/${encodeURIComponent(buildingIdForTrail)}?ss=${encodeURIComponent(ssCode)}`;
    }
    if (buildingIdForTrail) return `/jobs/building/${encodeURIComponent(buildingIdForTrail)}`;
    return '/jobs';
  }, [location.state, buildingIdForTrail, ssCode]);

  const navLabel = useMemo(() => (job ? formatSsFrequencyChip(job) : ''), [job]);

  const { data: issuedCerts = [] } = useQuery({
    queryKey: ['issued-forms', 'job-detail', buildingIdForTrail, ssCode],
    enabled: !!buildingIdForTrail && !!ssCode,
    queryFn: async () => {
      const res = await api.get('/paperwork/issued-forms', {
        params: {
          priority: 'date',
          buildingId: buildingIdForTrail,
          ssCodes: ssCode,
          limit: 200,
        },
      });
      return res.data?.data?.items || [];
    },
  });

  const openCertPreview = async (certId) => {
    const keepY = captureWindowScroll();
    pinWindowScroll(keepY, 1200);
    setCertPreviewId(certId);
    setCertPreviewHtml('');
    setCertSendTo('');
    try {
      const preview = await loadIssuedPaperworkPreview(api, { recordId: certId });
      setCertPreviewHtml(preview.html || '');
      pinWindowScroll(keepY, 1200);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Preview failed');
      setCertPreviewId(null);
      pinWindowScroll(keepY, 600);
    }
  };

  const closeCertPreviewAndReturn = () => {
    setCertPreviewId(null);
    setCertPreviewHtml('');
    const originTo = location.state?.origin?.to || ssListTo;
    navigate(originTo);
  };

  const sendCert = async () => {
    if (!certPreviewId) return;
    if (!certSendTo.trim()) {
      toast.error('Enter recipient email');
      return;
    }
    setCertBusy(true);
    try {
      await api.post(`/paperwork/issued-forms/${certPreviewId}/send`, { to: certSendTo.trim() });
      toast.success('Sent');
      setCertBusy(false);
      closeCertPreviewAndReturn();
    } catch (e) {
      setCertBusy(false);
      toast.error(e.response?.data?.error || e.message || 'Send failed');
    }
  };

  const printCert = () => {
    if (!certPreviewHtml) {
      toast.error('Preview still loading');
      return;
    }
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Popup blocked — allow popups to print');
      return;
    }
    w.document.write(certPreviewHtml);
    w.document.close();
    w.focus();
    w.print();
    closeCertPreviewAndReturn();
  };

  if (isLoading && !job) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Inspection not found</p>
        <button
          type="button"
          onClick={() => navigate('/jobs')}
          className="mt-4 text-primary-600 hover:text-primary-900"
        >
          Back to Inspections
        </button>
      </div>
    );
  }

  const daysUntilDueVal = daysUntilDue(applyInspectionDue(job).period_due);
  const statusStyle = getJobRowStyle(job.status, daysUntilDueVal);
  const nextStage = getNextStageAction(job.status);
  const busy = statusMutation.isPending || saveMutation.isPending || deleteMutation.isPending;

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingIdForTrail || job.building_id}
        buildingName={job.building_name}
        title={navLabel || INSPECTION_LABELS.plural}
        tabs={[]}
        infoTitle="Inspection ops story"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p>{JOB_DEFINITION.short}</p>
            <p>{INSPECTION_OPS_STORY.short}</p>
            <ul className="list-disc pl-5 space-y-1">
              {INSPECTION_OPS_STORY.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
            <p className="text-gray-600">
              Lead times: monthly {INSPECTION_OPS_STORY.leadTimes.MONTHLY}; quarterly{' '}
              {INSPECTION_OPS_STORY.leadTimes.QUARTERLY}; semi/annual{' '}
              {INSPECTION_OPS_STORY.leadTimes.ANNUAL}.
            </p>
            {job.status && JOB_STATUS_ACTORS[job.status] && (
              <p>
                <strong>Current:</strong> {JOB_STATUS_LABELS[job.status] || job.status} —{' '}
                {getAdminNextAction(job.status)}
              </p>
            )}
          </div>
        }
        actions={
          isAdmin ? (
            <>
              <button
                type="button"
                onClick={() => setEditing((v) => !v)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                <Pencil className="w-4 h-4 mr-2" />
                {editing ? 'Cancel edit' : 'Edit'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="flex items-center px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            </>
          ) : null
        }
      />

      {isAdmin && editing && (
        <form
          onSubmit={handleSaveEdit}
          className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-6 space-y-4"
        >
          <h2 className="text-lg font-semibold text-gray-900">Edit inspection</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due date</label>
              <input
                type="date"
                value={editForm.due_date}
                onChange={(e) => setEditForm((f) => ({ ...f, due_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled date</label>
              <input
                type="date"
                value={editForm.scheduled_date}
                onChange={(e) => setEditForm((f) => ({ ...f, scheduled_date: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {Object.keys(JOB_STATUS_LABELS).map((code) => (
                  <option key={code} value={code}>
                    {JOB_STATUS_LABELS[code]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned worker</label>
              <select
                value={editForm.assigned_worker_id}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, assigned_worker_id: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">— Not assigned —</option>
                {workersList.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name || w.username || w.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inspection type</label>
              <input
                type="text"
                value={editForm.inspection_type}
                onChange={(e) => setEditForm((f) => ({ ...f, inspection_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={editForm.inspection_frequency}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, inspection_frequency: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">—</option>
                <option value="MONTHLY">Monthly</option>
                <option value="QUARTERLY">Quarterly</option>
                <option value="SEMI_ANNUAL">Semi-annual</option>
                <option value="ANNUAL">Annual</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
            >
              Save changes
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <SsPeriodLine
        job={job}
        certificateLabel={
          issuedCerts[0]?.paperwork_label || issuedCerts[0]?.form_short || issuedCerts[0]?.paperwork_type || ''
        }
        onOpenCertificate={() => {
          if (issuedCerts[0]?.id) openCertPreview(issuedCerts[0].id);
        }}
        reports={issuedCerts}
        onOpenReport={(report) => {
          if (report?.id) openCertPreview(report.id);
        }}
      />
      {certPreviewId != null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
              <h3 className="font-semibold text-gray-900">Document preview</h3>
              <button
                type="button"
                onClick={() => {
                  setCertPreviewId(null);
                  setCertPreviewHtml('');
                }}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 min-h-[50vh]">
              {certPreviewHtml ? (
                <iframe title="Document preview" className="w-full min-h-[60vh] bg-white" srcDoc={certPreviewHtml} />
              ) : (
                <p className="p-8 text-center text-sm text-gray-500">Loading preview…</p>
              )}
            </div>
            <StickyActionFooter className="px-4">
              <div className="flex flex-wrap items-end gap-2">
                <input
                  type="email"
                  value={certSendTo}
                  onChange={(e) => setCertSendTo(e.target.value)}
                  placeholder="Recipient email"
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm flex-1 min-w-[200px]"
                />
                <button
                  type="button"
                  disabled={certBusy}
                  onClick={sendCert}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  <Send className="w-4 h-4" /> Send
                </button>
                <button
                  type="button"
                  onClick={printCert}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCertPreviewId(null);
                    setCertPreviewHtml('');
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50"
                >
                  OK
                </button>
              </div>
            </StickyActionFooter>
          </div>
        </div>
      )}

{showWorkerModal && (
        <WorkerSelectionModal
          job={job}
          onClose={() => setShowWorkerModal(false)}
          onSuccess={() => {
            setShowWorkerModal(false);
            invalidateJob();
          }}
        />
      )}

      {showScheduleModal && (
        <ScheduleJobModal
          job={job}
          onClose={() => {
            setShowScheduleModal(false);
            invalidateJob();
          }}
        />
      )}
    </div>
  );
}

export default JobDetail;
